import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { buildApiUrl } from "../config/api";
import "./AdminCustomers.css";

const money = (value, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: String(currency || "USD").toUpperCase() }).format(Number(value || 0));
const date = (value) => (value ? new Date(value).toLocaleString() : "Never");

function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [marketing, setMarketing] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadCustomers = useCallback(async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: String(page), limit: "25" });
      if (search.trim()) params.set("search", search.trim());
      if (status) params.set("status", status);
      if (marketing) params.set("marketing", marketing);
      const response = await fetch(buildApiUrl(`/admin/customers?${params}`), { credentials: "include" });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Could not load customers.");
      setCustomers(data.customers || []);
      setStats(data.stats || {});
      setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, status, marketing]);

  useEffect(() => {
    const timer = setTimeout(() => loadCustomers(1), 250);
    return () => clearTimeout(timer);
  }, [loadCustomers]);

  const openCustomer = async (customer) => {
    setSelected(customer);
    setDetail(null);
    setError("");
    try {
      const response = await fetch(buildApiUrl(`/admin/customers/${customer.id}`), { credentials: "include" });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Could not load customer details.");
      setDetail(data);
      setForm({
        fullName: data.customer.fullName,
        billingEmail: data.customer.billingEmail || "",
        status: data.customer.status,
        marketingOptIn: Boolean(data.customer.marketingOptIn),
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const saveCustomer = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      const response = await fetch(buildApiUrl(`/admin/customers/${selected.id}`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Could not update customer.");
      setMessage("Customer profile updated.");
      await Promise.all([loadCustomers(pagination.page), openCustomer(selected)]);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-customers-page">
      <aside className="admin-customers-sidebar">
        <div className="admin-customers-brand"><span>MayaChitro</span><small>Admin Panel</small></div>
        <nav>
          <Link to="/admin">Dashboard</Link>
          <Link to="/admin/products">Photos / Products</Link>
          <Link to="/admin/orders">Orders & Sales</Link>
          <Link className="active" to="/admin/customers">Customers</Link>
          <Link to="/admin/newsletter">Newsletter</Link>
          <Link to="/admin/hero-slides">Hero Slides</Link>
        </nav>
      </aside>

      <main className="admin-customers-main">
        <header className="admin-customers-header">
          <div><p>Customer intelligence</p><h1>Customers</h1><span>Search accounts, understand buying behavior, and manage access from one workspace.</span></div>
          <button onClick={() => loadCustomers(pagination.page)}>Refresh</button>
        </header>

        <section className="customer-stat-grid">
          <article><span>Total customers</span><strong>{stats.totalCustomers || 0}</strong></article>
          <article><span>Active</span><strong>{stats.activeCustomers || 0}</strong></article>
          <article><span>New · 30 days</span><strong>{stats.newLast30Days || 0}</strong></article>
          <article><span>Marketing opted in</span><strong>{stats.marketingCustomers || 0}</strong></article>
          <article><span>Blocked</span><strong>{stats.blockedCustomers || 0}</strong></article>
        </section>

        <section className="customer-workspace">
          <div className="customer-list-card">
            <div className="customer-filters">
              <label><span>Search</span><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email or customer UUID" /></label>
              <label><span>Status</span><select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All</option><option value="active">Active</option><option value="blocked">Blocked</option><option value="deleted">Deleted</option></select></label>
              <label><span>Marketing</span><select value={marketing} onChange={(e) => setMarketing(e.target.value)}><option value="">All</option><option value="yes">Opted in</option><option value="no">Not opted in</option></select></label>
            </div>
            {error && <div className="customer-alert error">{error}</div>}
            {message && <div className="customer-alert success">{message}</div>}
            <div className="customer-table-wrap">
              <table><thead><tr><th>Customer</th><th>Status</th><th>Orders</th><th>Lifetime value</th><th>Last activity</th></tr></thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer.id} className={selected?.id === customer.id ? "selected" : ""} onClick={() => openCustomer(customer)}>
                      <td><strong>{customer.fullName}</strong><small>{customer.email}</small></td>
                      <td><span className={`customer-status ${customer.status}`}>{customer.status}</span></td>
                      <td>{customer.orderCount}<small>{customer.wishlistCount} saved</small></td>
                      <td>{money(customer.lifetimeValue)}</td>
                      <td>{date(customer.lastOrderAt || customer.lastLoginAt)}</td>
                    </tr>
                  ))}
                  {!loading && !customers.length && <tr><td colSpan="5" className="customer-empty">No matching customers.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="customer-pagination"><span>{pagination.total || 0} customers</span><div><button disabled={pagination.page <= 1} onClick={() => loadCustomers(pagination.page - 1)}>Previous</button><span>{pagination.page} / {pagination.pages}</span><button disabled={pagination.page >= pagination.pages} onClick={() => loadCustomers(pagination.page + 1)}>Next</button></div></div>
          </div>

          <aside className="customer-detail-card">
            {!selected && <div className="customer-detail-empty"><strong>Select a customer</strong><p>Click a row to see account, orders, products, wishlist and download activity.</p></div>}
            {selected && !detail && <div className="customer-detail-empty"><strong>Loading profile…</strong></div>}
            {detail && form && <>
              <div className="customer-profile-head"><div className="customer-avatar">{detail.customer.fullName?.charAt(0) || "C"}</div><div><h2>{detail.customer.fullName}</h2><a href={`mailto:${detail.customer.email}`}>{detail.customer.email}</a><small>Joined {date(detail.customer.createdAt)}</small></div></div>
              <div className="customer-mini-stats"><div><strong>{detail.orders.length}</strong><span>Orders</span></div><div><strong>{money(detail.orders.filter(o => o.paymentStatus === "paid").reduce((sum, o) => sum + Number(o.amountTotal), 0))}</strong><span>Paid value</span></div><div><strong>{detail.downloads.reduce((sum, d) => sum + Number(d.downloadCount), 0)}</strong><span>Downloads</span></div></div>
              <form className="customer-edit-form" onSubmit={saveCustomer}>
                <h3>Account controls</h3>
                <label>Full name<input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></label>
                <label>Billing email<input type="email" value={form.billingEmail} onChange={(e) => setForm({ ...form, billingEmail: e.target.value })} /></label>
                <label>Status<select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="active">Active</option><option value="blocked">Blocked</option><option value="deleted">Deleted</option></select></label>
                <label className="customer-check"><input type="checkbox" checked={form.marketingOptIn} onChange={(e) => setForm({ ...form, marketingOptIn: e.target.checked })} /> Marketing opt-in</label>
                <button type="submit">Save customer</button>
              </form>
              <section className="customer-timeline"><h3>Purchased products</h3>{detail.products.map((item) => <article key={item.id}><div><strong>{item.productTitle}</strong><small>Order #{item.orderId} · {item.licenseLabel} · {money(item.lineTotal)}</small></div>{item.currentProductDbId ? <Link to={`/admin/products?edit=${item.currentProductDbId}`}>Edit product</Link> : <span>Archived</span>}</article>)}{!detail.products.length && <p>No purchases yet.</p>}</section>
              <section className="customer-timeline"><h3>Order history</h3>{detail.orders.map((order) => <article key={order.id}><div><strong>Order #{order.id}</strong><small>{date(order.createdAt)} · {order.itemCount} items · {order.downloadCount} downloads</small></div><span className={`customer-status ${order.paymentStatus}`}>{order.paymentStatus}</span></article>)}{!detail.orders.length && <p>No orders yet.</p>}</section>
              <section className="customer-timeline"><h3>Wishlist</h3>{detail.wishlist.map((item) => <article key={item.id}><div><strong>{item.productTitle}</strong><small>Saved {date(item.createdAt)}</small></div>{item.productDbId ? <Link to={`/admin/products?edit=${item.productDbId}`}>Edit product</Link> : <span>Unavailable</span>}</article>)}{!detail.wishlist.length && <p>No saved products.</p>}</section>
            </>}
          </aside>
        </section>
      </main>
    </div>
  );
}

export default AdminCustomers;
